/*
 * Copyright © 2017 - 2019 Atilika Inc. All rights reserved.
 */

import React, {memo, useCallback} from "react";
import {createUseStyles} from "react-jss";
import Button from "../common/Button";
import UI from "../common/UI";
import RadioOff from "../assets/images/icons/radio-off.svg";
import RadioOn from "../assets/images/icons/radio-on.svg";
import {Mode} from "./Kuromoji";
import {Language} from "../common/Language";

const useStyles = createUseStyles({
    form: {
        "& fieldset": {
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            border: 0,
            padding: 0,
            margin: 0,
            [UI.media.only("xs")]: {
                flexDirection: "column",
                alignItems: "stretch"
            }
        }
    },
    input: {
        margin: "0 0 1.5rem",
        [UI.media.gte("sm")]: {
            flexBasis: "70%"
        },
        "& textarea": {
            border: 0,
            height: 180,
            padding: "1rem",
            width: "100%"
        }
    },
    radioInput: {
        position: "absolute",
        left: -1000,
        "& + label": {
            background: `transparent url("${RadioOff}") no-repeat 0 2px`,
            fontSize: "1.375rem",
            fontFamily: UI.fonts.lato,
            backgroundSize: 20,
            padding: "0 0 0 2.5rem",
            cursor: "pointer",
            display: "inline-block",
            width: "auto",
            margin: "0 0 1.5rem"
        },
        "&:checked + label": {
            background: `transparent url("${RadioOn}") no-repeat 0 2px`,
            backgroundSize: 20
        }
    },
    error: {
        color: UI.colors.red.toCSS(),
        fontSize: ".75rem",
        lineHeight: 1,
        margin: ".25rem 0 0"
    },
    buttons: {
        display: "flex",
        justifyContent: "space-between",
        maxWidth: "70%",
        [UI.media.only("xs")]: {
            display: "flex",
            justifyContent: "center",
            maxWidth: "100%"
        },
        "& button": {
            marginRight: "2rem",
            textTransform: "lowercase",
            width: "100%",
            [UI.media.only("xs")]: {
                flexGrow: 1,
                flexBasis: 0
            },
            "&:last-child": {
                marginRight: 0
            }
        }
    },
    options: {
        display: "flex",
        flexDirection: "column",
        paddingLeft: "3rem",
        [UI.media.gte("sm")]: {
            flexBasis: "30%"
        },
        [UI.media.only("xs")]: {
            flexBasis: "100%",
            padding: 0,
            flexDirection: "row",
            justifyContent: "flex-start"
        },
        "& div": {
            minWidth: 150,
            display: "flex",
            flexDirection: "column"
        }
    }
});

const labels = {
    ja: {
        modes: {
            normal: "標準",
            search: "検索",
            extended: "拡張",
            viterbi: "ビタビ"
        },
        clear: "クリア",
        submit: "実行"
    },
    en: {
        modes: {
            normal: "Normal",
            search: "Search",
            extended: "Extended",
            viterbi: "Viterbi"
        },
        clear: "Clear",
        submit: "Tokenize"
    }
};

interface KuromojiDemoFormProps {
    text: string;
    mode: Mode;
    isProcessing: boolean;
    error?: string;
    lang: Language;
    onSubmit: () => void;
    onTextChange: (text: string) => void;
    onModeChange: (mode: Mode) => void;
    onClear: () => void;
}

function KuromojiDemoForm(props: KuromojiDemoFormProps) {
    const classes = useStyles();
    const {lang, isProcessing, text, mode, error, onTextChange, onModeChange, onSubmit} = props;

    const handleTextChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            onTextChange(event.target.value);
        },
        [onTextChange]
    );

    const handleChangeMode = (nextMode: Mode) => {
        return () => onModeChange(nextMode);
    };

    const handleSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            onSubmit();
        },
        [onSubmit]
    );

    return (
        <form className={classes.form} onSubmit={handleSubmit}>
            <fieldset>
                <div className={classes.input}>
                    <textarea value={text} onChange={handleTextChange} />
                    <p className={classes.error}>{error || <span>&nbsp;</span>}</p>
                </div>
                <div className={classes.options}>
                    <div>
                        <span>
                            <input
                                type="radio"
                                className={classes.radioInput}
                                id="normal"
                                checked={mode === Mode.Normal}
                                onChange={handleChangeMode(Mode.Normal)}
                                disabled={isProcessing}
                            />
                            <label htmlFor="normal">{labels[lang].modes.normal}</label>
                        </span>
                        <span>
                            <input
                                type="radio"
                                className={classes.radioInput}
                                id="search"
                                checked={mode === Mode.Search}
                                onChange={handleChangeMode(Mode.Search)}
                                disabled={isProcessing}
                            />
                            <label htmlFor="search">{labels[lang].modes.search}</label>
                        </span>
                    </div>
                    <div>
                        <span>
                            <input
                                type="radio"
                                className={classes.radioInput}
                                id="extended"
                                checked={mode === Mode.Extended}
                                onChange={handleChangeMode(Mode.Extended)}
                                disabled={isProcessing}
                            />
                            <label htmlFor="extended">{labels[lang].modes.extended}</label>
                        </span>
                        <span>
                            <input
                                type="radio"
                                className={classes.radioInput}
                                id="viterbi"
                                checked={mode === Mode.Viterbi}
                                onChange={handleChangeMode(Mode.Viterbi)}
                                disabled={isProcessing}
                            />
                            <label htmlFor="viterbi">{labels[lang].modes.viterbi}</label>
                        </span>
                    </div>
                </div>
            </fieldset>
            <div className={classes.buttons}>
                <Button type="button" onClick={props.onClear} disabled={isProcessing}>
                    {labels[lang].clear}
                </Button>
                <Button type="submit" disabled={isProcessing}>
                    {labels[lang].submit}
                </Button>
            </div>
        </form>
    );
}

export default memo(KuromojiDemoForm);
